/**
 * Elle Exist — quick add picker. Ported from the Nucery (Space) theme.
 *
 * When a card's bag is clicked and the product has options to choose, open a
 * popup (desktop modal / mobile bottom sheet) to pick the variant. Single-
 * variant cards are left alone: their bag is a Palo Alto product form and the
 * theme's cart adds them directly.
 *
 * Palo Alto changes: the picker's Add button is itself a product form button
 * carrying data-add-to-cart, so the theme's cart (listening on the document)
 * performs the add and opens its drawer; the picker closes on theme:cart:added.
 *
 * Trigger contract: an element matching TRIGGER_SELECTORS carrying
 * data-variant-count="N" and, ideally, data-product-handle="...". Without the
 * handle it is derived from the nearest a[href*="/products/"] in the card.
 */
(function () {
  'use strict';

  var TRIGGER_SELECTORS = '[data-ee-quick-add],[data-nqa-trigger],[data-cfs-add]';
  var modal, bodyEl, productCache = {};
  var current = { product: null, selected: [] };

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartAddUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || root() + 'cart/add';
  }

  function strings() {
    return (window.theme && window.theme.strings) || {};
  }

  /* ---------- money ---------- */
  function formatMoney(cents) {
    cents = Number(cents) || 0;
    var theme = window.theme;
    if (theme && typeof theme.formatMoney === 'function' && theme.moneyFormat) {
      var withCode = theme.settings && theme.settings.currency_code_enable;
      var s = theme.formatMoney(cents, withCode ? theme.moneyWithCurrencyFormat : theme.moneyFormat);
      var d = document.createElement('div');
      d.innerHTML = s;
      return (d.textContent || d.innerText || '').trim();
    }
    var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + active;
    }
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------- modal shell ---------- */
  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.id = 'EeQuickAdd';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="nqa__scrim" data-nqa-close></div>' +
      '<div class="nqa__dialog" role="dialog" aria-modal="true" aria-label="Choose options">' +
      '<button type="button" class="nqa__x" data-nqa-close aria-label="' + escapeAttr(strings().close || 'Close') + '">×</button>' +
      '<div class="nqa__grab" aria-hidden="true"></div>' +
      '<div class="nqa__body" data-nqa-body></div>' +
      '</div>';
    document.body.appendChild(modal);
    bodyEl = modal.querySelector('[data-nqa-body]');

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-nqa-close]')) { close(); return; }
      var optBtn = e.target.closest('.nqa__opt-btn');
      if (optBtn) { selectOption(optBtn); return; }
      // a click on [data-nqa-add] is left alone: it is a data-add-to-cart button
      // inside a product form, and Palo Alto's cart handles it on the document
    });

    // Palo Alto fires this once the item is in the cart, just before its drawer opens
    document.addEventListener('theme:cart:added', function () {
      if (modal && modal.classList.contains('is-open')) close();
    });
    return modal;
  }

  function openModal() {
    ensureModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('nqa-lock');
  }
  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('nqa-lock');
  }

  /* ---------- product option helpers ---------- */
  function optionNames(product) {
    var opts = product.options || [];
    return opts.map(function (o) { return (o && o.name) ? o.name : o; });
  }
  function variantOptions(v) {
    if (v.options && v.options.length) return v.options;
    var out = [];
    if (v.option1 != null) out.push(v.option1);
    if (v.option2 != null) out.push(v.option2);
    if (v.option3 != null) out.push(v.option3);
    return out;
  }
  function valuesForOption(product, idx) {
    var seen = {}, out = [];
    (product.variants || []).forEach(function (v) {
      var val = variantOptions(v)[idx];
      if (val != null && !seen[val]) { seen[val] = 1; out.push(val); }
    });
    return out;
  }
  function firstAvailable(product) {
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) { if (vs[i].available) return vs[i]; }
    return vs[0] || null;
  }
  function isDefaultOption(name, vals) {
    return String(name).toLowerCase() === 'title' &&
      vals.length <= 1 && (!vals.length || /default/i.test(String(vals[0])));
  }
  function hasRealOptions(product) {
    var names = optionNames(product);
    if (!names.length) return false;
    for (var i = 0; i < names.length; i++) {
      if (!isDefaultOption(names[i], valuesForOption(product, i))) return true;
    }
    return false;
  }
  function resolveVariant(product, selected) {
    return (product.variants || []).filter(function (v) {
      return variantOptions(v).every(function (val, i) {
        return selected[i] == null || String(selected[i]) === String(val);
      });
    })[0] || null;
  }
  function valueAvailable(product, idx, value, selected) {
    return (product.variants || []).some(function (v) {
      var vo = variantOptions(v);
      if (String(vo[idx]) !== String(value)) return false;
      for (var i = 0; i < selected.length; i++) {
        if (i !== idx && selected[i] != null && String(selected[i]) !== String(vo[i])) return false;
      }
      return v.available;
    });
  }

  /* ---------- render ---------- */
  function featuredUrl(product) {
    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    if (img && img.indexOf('width=') === -1) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=300';
    return img;
  }

  function render() {
    var p = current.product;
    if (!p || !bodyEl) return;
    var s = strings();
    var names = optionNames(p);
    var optsHtml = names.map(function (name, idx) {
      var vals = valuesForOption(p, idx);
      if (!vals.length || isDefaultOption(name, vals)) return '';
      var pills = vals.map(function (val) {
        var sel = String(current.selected[idx]) === String(val) ? ' sel' : '';
        var avail = valueAvailable(p, idx, val, current.selected);
        return '<button type="button" class="nqa__opt-btn' + sel + (avail ? '' : ' soldout') +
          '" data-nqa-opt="' + idx + '" data-nqa-val="' + escapeAttr(val) + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="nqa__opt"><div class="nqa__opt-name">' + escapeHtml(name) + '</div><div class="nqa__opts">' + pills + '</div></div>';
    }).join('');

    var v = resolveVariant(p, current.selected);
    var priceHtml = '';
    if (v) {
      priceHtml = '<div class="nqa__price">' + escapeHtml(formatMoney(v.price));
      if (v.compare_at_price && v.compare_at_price > v.price) priceHtml += ' <s>' + escapeHtml(formatMoney(v.compare_at_price)) + '</s>';
      priceHtml += '</div>';
    }
    var img = featuredUrl(p);
    var canAdd = v && v.available;
    var addLabel = canAdd ? (s.add_to_cart || 'Add to cart') : (v ? (s.sold_out || 'Sold out') : (s.unavailable || 'Select options'));

    // The Add button is a Palo Alto product form: data-add-to-cart makes the
    // theme's cart add it and open the drawer; data-upsell-btn keeps the page's
    // own product form from touching it.
    var formHtml =
      '<form method="post" action="' + escapeAttr(cartAddUrl()) + '" class="nqa__form">' +
      '<input type="hidden" name="id" value="' + (v ? v.id : '') + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<button type="submit" name="add" class="nqa__add" data-nqa-add data-add-to-cart data-upsell-btn' + (canAdd ? '' : ' disabled') + '>' + escapeHtml(addLabel) + '</button>' +
      '<div class="nqa__errors" data-cart-errors-container role="alert"></div>' +
      '</form>';

    bodyEl.innerHTML =
      '<div class="nqa__media">' + (img ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(p.title) + '">' : '') + '</div>' +
      '<div class="nqa__info">' +
        (p.type ? '<div class="nqa__eyebrow">' + escapeHtml(p.type) + '</div>' : '') +
        '<h3 class="nqa__title">' + escapeHtml(p.title) + '</h3>' +
        priceHtml +
        optsHtml +
        formHtml +
      '</div>';
  }

  function selectOption(btn) {
    if (btn.classList.contains('soldout')) return;
    var idx = parseInt(btn.getAttribute('data-nqa-opt'), 10);
    var val = btn.getAttribute('data-nqa-val');
    current.selected[idx] = val;
    render();
  }

  // Add one variant through Palo Alto's cart without a visible picker: a
  // throwaway product form is clicked, so the theme's own add flow runs.
  function addVariant(variantId) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = cartAddUrl();
    form.className = 'nqa__ghost-form';
    form.setAttribute('aria-hidden', 'true');
    form.innerHTML =
      '<input type="hidden" name="id" value="' + variantId + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<button type="submit" name="add" data-add-to-cart data-upsell-btn tabindex="-1">Add</button>';
    document.body.appendChild(form);
    form.querySelector('[data-add-to-cart]').click();
    setTimeout(function () { form.remove(); }, 8000);
  }

  /* ---------- open by handle ----------
   * decide=true  → only an optionless, in-stock product is added straight away;
   *                anything with real options (or sold out) opens the picker.
   * decide=false → always open the picker.
   */
  function openByHandle(handle, preselectVariantId, decide) {
    if (!handle) return;
    var url = root() + 'products/' + encodeURIComponent(handle) + '.js';

    var act = function (p) {
      if (decide && !hasRealOptions(p)) {
        var only = firstAvailable(p);
        if (only && only.available) { addVariant(only.id); return; }
      }
      openModal();
      current.product = p;
      var base = preselectVariantId && (p.variants || []).filter(function (v) { return String(v.id) === String(preselectVariantId); })[0];
      base = base || firstAvailable(p);
      current.selected = base ? variantOptions(base).slice() : [];
      render();
    };

    if (productCache[handle]) { act(productCache[handle]); return; }
    if (!decide) {
      openModal();
      if (bodyEl) bodyEl.innerHTML = '<div class="nqa__info"><h3 class="nqa__title">…</h3></div>';
    }
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) { productCache[handle] = p; act(p); })
      .catch(function () { if (!decide) close(); });
  }

  /* ---------- trigger detection ---------- */
  function handleFromCard(btn) {
    if (btn.getAttribute('data-product-handle')) return btn.getAttribute('data-product-handle');
    var url = btn.getAttribute('data-product-url') || '';
    if (!url) {
      var card = btn.closest('article, li, .grid-product, .bestsellers-card, .ee-product-rail__card, .ncs-card, [data-product-url]');
      var link = card && card.querySelector('a[href*="/products/"]');
      if (link) url = link.getAttribute('href') || '';
    }
    var m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function bind() {
    if (bind._bound) return;
    bind._bound = true;
    // Capture phase, so a multi-variant bag is intercepted before Palo Alto's
    // document-level cart handler would add the first variant blindly.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest(TRIGGER_SELECTORS);
      if (!btn) return;
      var count = parseInt(btn.getAttribute('data-variant-count'), 10) || 0;
      var decide = btn.hasAttribute('data-nqa-decide');
      if (count <= 1 && !decide) return; // single variant → Palo Alto's cart adds it
      var handle = handleFromCard(btn);
      if (!handle) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openByHandle(handle, btn.getAttribute('data-variant-id'), decide);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  window.EeQuickAdd = { open: openByHandle, close: close };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
